<template lang="pug">
    #member-center-admin-user-comments
      WtTile(parent :col="12").no-padding

        #loader(v-if="showSpinner")
          BLoading(
            :active.sync="showSpinner"
            :is-full-page="false"
          )
            //- note, WtTile employs the same template as this
            .is-syncing-loader.has-text-weight-bold.is-size-1 Syncing..

        WtTile(child :col="5")
          BField(label="Select Challenge")
            BAutocomplete.autocomplete(
                id="challenge"
                class="custom-autocomplete"
                :data="filteredChallenges"
                v-model="selectedChallenge"
                :open-on-focus="true"
                :clearable="true"
                :custom-formatter="challenge => formatChallengeName(challenge)"
                placeholder="Search challenge..."
                dropdown-position="bottom"
                :icon="selectedChallengeIsFeatured ? 'star' : ''"
                @select="value => handleChallengeSelect(value)"
            )
                template(slot-scope="props")
                  .media
                    BIcon.dropdown-star-icon(icon="star" v-if="props.option.featured")
                    .media-content
                      .content
                        div.ellipsis {{formatChallengeName(props.option)}}

        WtTile(child :col="3")
          BField(label="Search by Username")
            BAutocomplete.autocomplete(
                id="username"
                class="custom-autocomplete"
                icon="magnify"
                v-model="selectedUsername"
                :open-on-focus="true"
                :data="memberSearchResult"
                :clearable="true"
                placeholder="Search..."
                @typing="onSearch"
                dropdown-position="bottom"
                @select="member => selectMember(member)"
            )
              template(slot-scope="props")
                | {{ props.option.username }}

        WtTile(child :col="2")
          BField(label="Start Date")
            BDatepicker(
                :min-date="programStartDate"
                :max-date="new Date()"
                icon="calendar-today"
                editable
                v-model="userCommentsStartDate"
                :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
                :events="startDatePickerEvents"
                position="is-top-right"
            )
        WtTile(child :col="2")
          BField(label="End Date")
            BDatepicker(
                :min-date="userCommentsStartDate ? userCommentsStartDate : programStartDate"
                :max-date="new Date()"
                icon="calendar-today"
                editable
                v-model="userCommentsEndDate"
                :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
                :events="endDatePickerEvents"
                position="is-top-left"
            )
          
      WtTile(parent :col="12").no-padding
        .level-right.is-flex-grow-1
          WtTile(child :col="2")
            WtButton.post-button(
              semibold
              :isRouterLink="false"
              :isText="true"
              color="primary"
              @click.native="resetFilters"
              ) Reset Filters
          WtTile(child :col="2")
            WtButton(
                semibold
                :isRouterLink="false"
                :isText="true"
                color="primary"
                :disabled="processing"
                @click.native="handleFetchComments"
                ) Apply Filters
      template(v-if="userComments && selectedChallenge")
        WtTitle.m-3 Comments ( {{ paginationObject.total }} )
            WtTile
            .card
              .scrollable-container
                span(
                    v-if="!_.isEmpty(userComments.data)"
                    v-for="item in userComments.data"
                    :key="item.id"
                ) 
                    .media.mt-3
                    MemberCenterAdminUserComment(
                        :data="item"
                        :challenge-name="challengeName"
                        :challenge-id="challengeId"
                        :delete-discussion-post-action="deleteDiscussionPostAction"
                        :page-info="{ photosOnly, currentPage }"
                    )
              .empty-state(v-if="_.isEmpty(userComments.data)").m-4
                p Hey there,
                p No user comments with above filters
              WtPagination.m-1(
                :pagination-object="paginationObject"
                :action="fetchItems"
                order="is-centered"
              )

  </template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtMediaObject from "@@/WtMediaObject/WtMediaObject";
import WtPagination from "@@/WtPagination/WtPagination";
import WtRouterLink from "@@/WtRouterLink/WtRouterLink";
import MemberCenterAdminUserComment from "@@/MemberCenterAdmin/MemberCenterAdminUserComment";
import debounce from "lodash/debounce";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";

export default {
  name: "MemberCenterAdminUserComments",

  components: {
    WtTitle,
    WtTile,
    WtButton,
    WtMediaObject,
    WtPagination,
    MemberCenterAdminUserComment,
    EntityAvatar,
    WtRouterLink
  },

  mixins: [dateUtilityMixin],

  data() {
    return {
      userCommentsStartDate: null,
      userCommentsEndDate: null,
      selectedChallenge: null,
      challengeName: "",
      selectedChallengeIsFeatured: false,
      showSpinner: false,
      userComments: null,
      challengeId: null,
      photosOnly: false,
      query: "",
      selectedMember: null,
      memberSearchResult: [],
      applyFilters: true,
      perPage: 20,
      processing: false,
      dataObject: null
    };
  },

  computed: {
    ...mapGetters("program", [
      "allChallenges",
      "allChallengesLoaded",
      "coreProgramMeta"
    ]),
    ...mapGetters("challenges", ["getDiscussionById"]),

    selectedUsername: {
      get() {
        return this._.get(this.selectedMember, "username", "");
      },
      set() {}
    },

    currentPage() {
      return this._.get(this.userComments, "paginationObject.currentPage", 1);
    },

    paginationObject() {
      return this._.get(this.userComments, "pagination", null);
    },

    programStartDate() {
      return new Date(
        new Date(this.coreProgramMeta.startDate).setHours(0, 0, 0, 0)
      );
    },

    endDatePickerEvents() {
      return this.startDate ? [this.startDate] : null;
    },

    startDatePickerEvents() {
      return this.endDate ? [this.endDate] : null;
    },

    filteredChallenges() {
      let challenges = this.allChallenges;

      if (!this.coreProgramMeta.isWellnessMetricsOn) {
        challenges = challenges.filter(option => {
          return !(option.isCustomWellness === 1 || option.isWellness === 1);
        });
      }

      if (this.selectedChallenge) {
        return challenges.filter(option => {
          return (
            option.name
              .toString()
              .toLowerCase()
              .indexOf(this.selectedChallenge.toLowerCase()) >= 0
          );
        });
      } else {
        return challenges;
      }
    }
  },

  mounted() {
    const programId = this.$auth.user().programId;
    this.fetchAllChallenges({ programId });

    this.onSearch = debounce(async query => {
      if (query[query.length - 1] === " ") {
        return;
      }

      if (query.length === 0) {
        return;
      }

      const response = await this.searchMembers({
        programId: this.$auth.user().programId,
        query: query,
        perPage: this.perPage
      });

      this.memberSearchResult = response.data;
    }, 400);
  },

  methods: {
    ...mapActions("program", ["fetchAllChallenges", "searchMembers"]),
    ...mapActions("challenges", [
      "fetchChallengeDiscussion",
      "deleteChallengeDiscussionPost"
    ]),
    ...mapActions(["snackBar"]),

    resetFilters() {
      this.selectedChallenge = null;
      this.selectedMember = null;
      this.userCommentsStartDate = null;
      this.userCommentsEndDate = null;
      this.userComments = null;
    },

    onSearch() {},

    handleChallengeSelect(item) {
      if (item) {
        this.challengeId = item.id;
        this.challengeName = item.name;
        this.selectedChallengeIsFeatured = item.featured;
      }
    },

    selectMember(item) {
      if (item) {
        this.selectedMember = item;
      }
    },

    async handleFetchComments(page = null) {
      this.processing = true;
      try {
        let params = {
          perPage: this.perPage,
          challengeId: this.challengeId,
          programId: this.coreProgramMeta.id,
          selectedMemberId: this._.get(this.selectedMember, "id", null),
          startDate: this.userCommentsStartDate
            ? this.formatDateForRequest(this.userCommentsStartDate)
            : this.coreProgramMeta.startDate,
          endDate: this.userCommentsEndDate
            ? this.formatDateForRequest(this.userCommentsEndDate)
            : this.today().formatted
        };

        page = page ? `&page=${page}` : "";

        const response = await this.axios.get(
          `discussions/admin-user-comments?${page}`,
          {
            params
          }
        );
        this.applyFilters = false;
        this.processing = false;
        this.userComments = response.data;
      } catch (error) {
        this.snackBar({
          duration: 10000,
          type: "is-danger",
          message: "Please select a challenge"
        });
        this.processing = false;
      }
    },

    fetchItems(event) {
      return this.handleFetchComments(event);
    },

    async deleteDiscussionPostAction(discussionPostId) {
      this.$buefy.dialog.confirm({
        title: "Delete Discussion Post",
        confirmText: "Delete Discussion Post",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want to delete this discussion post?",
        onConfirm: async () => {
          await this.deleteChallengeDiscussionPost(discussionPostId);
          this.handleFetchComments();
        }
      });
    },

    formatChallengeName(challenge) {
      const statusArray = ["Pending", "Active", "Completed"];
      let formattedName = `${challenge.name} | `;
      if (challenge.featured) {
        formattedName = `Featured: ${formattedName}`;
      }
      let startDate = this.moment(challenge.startDate).format("MMM DD, YYYY");
      let endDate = this.moment(challenge.endDate).format("MMM DD, YYYY");
      formattedName += `${startDate} - ${endDate} | ${
        statusArray[challenge.status]
      }`;
      return formattedName;
    }
  }
};
</script>

<style lang="sass" scoped>
#member-center-admin-user-comments
  .title
    margin-bottom: 0px
  small
    color: $grey
    font-size: 12px
  .is-grey
    color: $grey
  .card
    background-color: $grey-lighter
  .no-padding
    padding: 0px !important
  .dropdown-star-icon
    margin-left: -5px
  .media-content
    max-width: 100%
  .ellipsis
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
  .ellipsis:hover
    text-overflow: clip
    white-space: normal
    word-break: break-all
  .card
    overflow: visible
  .scrollable-container
    max-height: 800px
    overflow-x: auto
    scrollbar-width: thin
    -webkit-overflow-scrolling: touch
    ::-webkit-scrollbar
        width: 8px
        background-color: #f5f5f5
        border-radius: 4px
    ::-webkit-scrollbar-thumb
        border-radius: 4px
</style>
